import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { LogoMark } from "../components/Logo";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-[#A3A3A3] hover:text-white" data-testid="back-link">
            <ArrowLeft size={20} weight="bold" /> Indietro
          </Link>
          <div className="flex items-center gap-2">
            <LogoMark size={28} />
            <span className="font-heading font-black text-xl uppercase tracking-tight">
              ETA<span className="text-[#FFD600]">Build</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 prose prose-invert" data-testid="privacy-policy">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Documento legale</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">
            Privacy Policy & GDPR
          </h1>
          <p className="text-xs text-[#A3A3A3] mt-2">Ultimo aggiornamento: febbraio 2026</p>
        </div>

        <div className="space-y-5 text-sm text-[#D4D4D4] leading-relaxed">
          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">1. Titolare del trattamento</h2>
            <p>
              Titolare del trattamento dei dati personali è <b>ETA Build</b> (in seguito anche "App",
              "Servizio" o "Titolare"), contattabile all'indirizzo email{" "}
              <a href="mailto:lucaroc2@gmail.com" className="text-[#FFD600]">lucaroc2@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">2. Dati raccolti</h2>
            <p>L'App raccoglie e tratta le seguenti categorie di dati:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><b>Dati di registrazione</b>: nome, indirizzo email, password (cifrata con bcrypt).</li>
              <li><b>Dati aziendali</b>: ragione sociale, P.IVA, settore di attività.</li>
              <li><b>Dati operativi</b>: preventivi, foto di cantiere, trascrizioni vocali, computi metrici, anagrafica dipendenti, attestati professionali, timbrature.</li>
              <li><b>Dati di pagamento</b>: gestiti esclusivamente da Stripe (PCI-DSS Level 1); ETA Build non memorizza dati di carta.</li>
              <li><b>Dati tecnici</b>: indirizzo IP, browser, log applicativi necessari alla sicurezza.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">2-bis. Dati dei dipendenti</h2>
            <p>
              I dati dei dipendenti caricati dal Titolare del contratto (nome, cognome, ruolo, telefono,
              email, foto profilo, attestati professionali con data di scadenza, timbrature di entrata/uscita)
              sono trattati dal Titolare aziendale che li carica, in qualità di <b>autonomo titolare del trattamento</b>
              nei confronti dei propri dipendenti, e da ETA Build in qualità di <b>responsabile esterno del trattamento</b>
              ai sensi dell'art. 28 GDPR.
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Finalità: gestione operativa cantiere, monitoraggio scadenze attestati, conteggio ore lavorate.</li>
              <li>Accesso: solo l'utente Titolare che ha caricato i dati. Nessuna condivisione con altri utenti ETA Build.</li>
              <li>Conservazione: per la durata del rapporto di lavoro + 5 anni per fini fiscali/contributivi.</li>
              <li>Diritti dell'interessato: i dipendenti possono esercitare i propri diritti GDPR contattando il proprio datore di lavoro (Titolare effettivo).</li>
              <li>Sicurezza: tutti i dati sono cifrati in transito (HTTPS/TLS) e isolati per <code>user_id</code> nel database.</li>
            </ul>
            <p className="text-xs text-[#A3A3A3] mt-2">
              Il Titolare del contratto si impegna a informare i propri dipendenti del trattamento
              e a raccogliere il consenso/base giuridica quando richiesto dalla normativa.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">3. Finalità e base giuridica</h2>
            <p>I dati sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Erogare il Servizio richiesto (esecuzione del contratto – art. 6.1.b GDPR).</li>
              <li>Adempiere a obblighi fiscali e contabili (obbligo di legge – art. 6.1.c GDPR).</li>
              <li>Migliorare il Servizio e prevenire abusi (legittimo interesse – art. 6.1.f GDPR).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">4. Trattamento da parte di terzi (sub-responsabili)</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>MongoDB Atlas / hosting</b>: archiviazione cifrata dei dati applicativi.</li>
              <li><b>Stripe Payments Europe Ltd.</b> (Dublino, IE): gestione pagamenti, abbonamenti ricorrenti, coupon e promotion codes. Stripe è certificata <b>PCI-DSS Level 1</b>: i numeri di carta non transitano mai sui server ETA Build e non sono memorizzati nel nostro database. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-[#FFD600]">Privacy Stripe</a>.</li>
              <li><b>Resend</b>: invio email transazionali (PDF preventivi, reset password, alert scadenze).</li>
              <li><b>Google Gemini (Nano Banana)</b> e <b>OpenAI (Whisper, GPT-4o)</b>: elaborazione AI delle trascrizioni vocali, analisi foto e generazione overlay. Le foto inviate alla AI <b>non vengono utilizzate per addestramento</b> dei modelli secondo i termini Enterprise.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">5. Periodo di conservazione</h2>
            <p>
              I dati sono conservati per la durata dell'account utente più i tempi richiesti dalla
              normativa (10 anni per documenti fiscali ex art. 2220 c.c.). In caso di cancellazione
              dell'account, i dati operativi sono rimossi entro 30 giorni; i log tecnici sono
              conservati per 6 mesi a fini di sicurezza.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">6. Diritti dell'interessato</h2>
            <p>Ai sensi degli artt. 15-22 GDPR hai diritto di:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><b>Accesso</b> ai tuoi dati personali.</li>
              <li><b>Rettifica</b> di dati inesatti o incompleti.</li>
              <li><b>Cancellazione</b> ("diritto all'oblio") tramite il pulsante <b>"Elimina Account"</b> presente nel tuo profilo, oppure scrivendo a{" "}
                <a href="mailto:lucaroc2@gmail.com" className="text-[#FFD600]">lucaroc2@gmail.com</a>.
              </li>
              <li><b>Limitazione</b> del trattamento.</li>
              <li><b>Portabilità</b> dei dati in formato strutturato (JSON).</li>
              <li><b>Opposizione</b> al trattamento basato su legittimo interesse.</li>
              <li><b>Reclamo</b> al Garante Privacy (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-[#FFD600]">garanteprivacy.it</a>).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">7. Sicurezza dei dati</h2>
            <p>
              Tutte le comunicazioni avvengono in HTTPS/TLS. Le password sono cifrate con bcrypt.
              L'accesso ai dati di ogni utente è isolato tramite autenticazione JWT e filtri
              applicativi per <code>user_id</code>; nessun utente può accedere ai dati di un altro
              account.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">8. Cookie</h2>
            <p>
              L'App utilizza solo cookie tecnici e di sessione necessari al funzionamento; non sono
              presenti cookie di profilazione o di marketing di terze parti.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-black uppercase text-[#FFD600] mb-2">9. Modifiche</h2>
            <p>
              ETA Build si riserva di aggiornare la presente Privacy Policy. Le modifiche
              sostanziali saranno comunicate via email registrata.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
